"use client";

import React from "react";
import { Activity, ArrowRight, CircleCheck as CheckCircle2, Cpu, FileText, Radio, ShieldAlert, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const specifications = [{
  label: "Detection Speed",
  value: "Application dependent"
}, {
  label: "Sensitivity Level",
  value: "Sub-nanogram (pg - ng)"
}, {
  label: "Ionization Source",
  value: "Non-Radioactive"
}, {
  label: "Target Library",
  value: "Military, Commercial & HME"
}];
export default function TraceHero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="traceexplosiveshero" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 lg:py-28">
      {/* Subtle Background Grid Pattern */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,var(--color-primary)/0.06,transparent_50%)]" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.03] bg-[linear-gradient(to_right,var(--color-foreground)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-foreground)_1px,transparent_1px)] bg-[size:40px_40px]" aria-hidden="true" />

      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8 xl:gap-12">
          {/* Left Column (7 Columns) */}
          <motion.div variants={containerVariants} initial="hidden" animate="visible" className="flex flex-col space-y-6 lg:col-span-7">
            {/* Eyebrow */}
            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 font-mono text-xs uppercase tracking-wider">
                Safety &amp; Security Family
              </Badge>
              <span className="text-xs font-mono text-muted-foreground">//</span>
              <span className="text-xs font-medium uppercase tracking-wider text-primary">
                Trace Explosives Detection
              </span>
            </motion.div>

            {/* H1 Title */}
            <motion.h1 variants={itemVariants} className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:leading-[1.15]">
              Rapid, high-sensitivity trace explosives detection for critical checkpoints.
            </motion.h1>

            {/* Lead Paragraph */}
            <motion.p variants={itemVariants} className="max-w-2xl text-base text-muted-foreground sm:text-lg md:leading-relaxed">
              Antech Solutions provides certified, non-radioactive trace detection systems engineered
              for airport security, border control, critical infrastructure, and defense operations.
              Achieve immediate identification of military-grade, commercial, and homemade explosive
              threats with minimal false alarms and comprehensive lifecycle validation across Ireland and the UK.
            </motion.p>

            {/* Technical Highlights / Specs */}
            <motion.div variants={itemVariants} className="grid grid-cols-2 gap-3 pt-2 sm:grid-cols-2 sm:gap-4 md:max-w-xl">
              {specifications.map((spec, i) => <div key={spec.label} data-index={i} className="flex flex-col rounded-lg border border-border bg-card/60 p-3 shadow-2xs backdrop-blur-xs">
                  <span className="text-xs font-medium text-muted-foreground">
                    {spec.label}
                  </span>
                  <span className="text-sm font-semibold text-foreground">
                    {spec.value}
                  </span>
                </div>)}
            </motion.div>

            {/* Dual CTA Actions */}
            <motion.div variants={itemVariants} className="flex flex-col gap-3 pt-4 sm:flex-row sm:items-center sm:gap-4">
              <Button asChild size="lg" className="font-semibold shadow-sm">
                <Link to="/BookADemoPage">
                  Book a Demonstration
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="font-semibold">
                <Link to="/DiscussYourApplicationPage">
                  <FileText className="size-4" />
                  Discuss Your Application
                </Link>
              </Button>
            </motion.div>

            {/* Trust and Compliance Marker */}
            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-4 pt-2 text-xs text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="size-3.5 text-primary" />
                <span>ECAC &amp; TSA Compliant</span>
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="size-3.5 text-primary" />
                <span>ISO 17025 Qualification</span>
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="size-3.5 text-primary" />
                <span>Field Support UK &amp; Ireland</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Column (5 Columns) - Instrument Schematic & Telemetry Card */}
          <motion.div initial={{
          opacity: 0,
          scale: shouldReduceMotion ? 1 : 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          ease: "easeOut",
          delay: 0.2
        }} className="lg:col-span-5">
            <div className="relative mx-auto w-full max-w-lg rounded-xl border border-border bg-card p-5 shadow-lg lg:max-w-none">
              {/* Card Header with Status Indicators */}
              <div className="flex items-center justify-between border-b border-border pb-4">
                <div className="flex items-center gap-2.5">
                  <div className="flex size-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Radio className="size-4 animate-pulse" />
                  </div>
                  <div>
                    <h2 className="font-mono text-xs font-bold uppercase tracking-wider text-foreground">
                      TRACE-IMS // System 400X
                    </h2>
                    <p className="text-[11px] text-muted-foreground">Ion Mobility Spectrometry Module</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1 text-[11px] font-medium text-secondary-foreground">
                  <span className="size-2 rounded-full bg-primary" />
                  <span>ONLINE // READY</span>
                </div>
              </div>

              {/* Instrument Visual Schematic Box */}
              <div className="relative my-5 overflow-hidden rounded-lg border border-border/80 bg-muted/40 p-5">
                {/* Visual Grid & Vector Schematic Lines */}
                <div className="relative flex flex-col items-center justify-center py-4">
                  <div className="relative flex w-full max-w-[280px] flex-col items-center">
                    {/* Sampling Inlet */}
                    <div className="relative z-10 flex h-7 w-20 items-center justify-center rounded-t-md border border-border bg-card text-[10px] font-mono font-medium text-foreground shadow-2xs">
                      SWAB INLET
                    </div>
                    {/* Thermal Desorber Section */}
                    <div className="relative z-10 flex h-10 w-36 items-center justify-center border-x border-b border-border bg-secondary/80 text-center text-[10px] font-mono font-semibold text-secondary-foreground">
                      THERMAL DESORBER
                      <span className="absolute -right-2 top-1.5 size-1.5 rounded-full bg-primary" />
                    </div>
                    {/* Ionization Chamber */}
                    <div className="relative z-10 flex h-14 w-48 flex-col items-center justify-center border-x border-b border-border bg-card p-1 shadow-xs">
                      <span className="text-[9px] font-mono text-muted-foreground">NON-RAD IONIZATION</span>
                      <div className="my-1 h-1 w-full rounded-full bg-muted overflow-hidden">
                        <div className="h-full w-2/3 bg-primary" />
                      </div>
                      <span className="text-[9px] font-mono font-medium text-foreground">PLASMA CORONA SOURCE</span>
                    </div>
                    {/* Drift Tube Spectrometer */}
                    <div className="relative z-10 flex h-16 w-56 flex-col items-center justify-center rounded-b-md border-x border-b border-border bg-secondary/60 text-center">
                      <div className="flex items-center gap-1.5 text-[10px] font-mono font-semibold text-foreground">
                        <Cpu className="size-3 text-primary" />
                        <span>DRIFT TUBE CHAMBER</span>
                      </div>
                      <span className="text-[9px] font-mono text-muted-foreground">HIGH-RESOLUTION TIME OF FLIGHT</span>
                    </div>

                    {/* Decorative Target Ion Beams */}
                    <div className="pointer-events-none absolute -inset-x-6 inset-y-2 border-dashed border-t border-border opacity-60" aria-hidden="true" />
                  </div>
                </div>

                {/* Live Diagnostic Overlay Metrics */}
                <div className="mt-3 grid grid-cols-3 gap-2 border-t border-border/60 pt-3 text-center">
                  <div className="rounded bg-card/80 p-1.5">
                    <span className="block text-[9px] font-mono text-muted-foreground">DRIFT TEMP</span>
                    <span className="text-xs font-mono font-bold text-foreground">185 °C</span>
                  </div>
                  <div className="rounded bg-card/80 p-1.5">
                    <span className="block text-[9px] font-mono text-muted-foreground">FLOW RATE</span>
                    <span className="text-xs font-mono font-bold text-foreground">220 mL/m</span>
                  </div>
                  <div className="rounded bg-card/80 p-1.5">
                    <span className="block text-[9px] font-mono text-muted-foreground">CLEAR TIME</span>
                    <span className="text-xs font-mono font-bold text-primary">&lt; 3.0 s</span>
                  </div>
                </div>
              </div>

              {/* Threat Libraries & Capabilities */}
              <div className="space-y-2.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-1.5 font-medium text-foreground">
                    <ShieldAlert className="size-3.5 text-primary" />
                    Target Library Coverage
                  </span>
                  <span className="font-mono text-[11px] text-muted-foreground">42 Threat Classes</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <span className="rounded-md border border-border bg-secondary/50 px-2 py-0.5 font-mono text-[10px] text-secondary-foreground">
                    TNT / RDX / PETN
                  </span>
                  <span className="rounded-md border border-border bg-secondary/50 px-2 py-0.5 font-mono text-[10px] text-secondary-foreground">
                    TATP / HMTD (Peroxides)
                  </span>
                  <span className="rounded-md border border-border bg-secondary/50 px-2 py-0.5 font-mono text-[10px] text-secondary-foreground">
                    Nitrates &amp; Chlorates
                  </span>
                  <span className="rounded-md border border-border bg-secondary/50 px-2 py-0.5 font-mono text-[10px] text-secondary-foreground">
                    Semtex &amp; C-4
                  </span>
                </div>
              </div>

              {/* Card Footer Quick Link */}
              <div className="mt-5 flex items-center justify-between border-t border-border pt-3">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Activity className="size-3.5 text-primary" />
                  <span>Real-time calibration active</span>
                </div>
                <Button asChild variant="link" size="sm" className="h-auto p-0 text-xs font-semibold">
                  <Link to="/SafetyAndSecurityPage">
                    View full security portfolio &rarr;
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}